import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";

import { refreshBokunCache } from "@/lib/bokun.functions";
import { SiteHeader } from "@/components/site/SiteHeader";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/admin/bokun-sync")({
  head: () => ({
    meta: [
      { title: "Admin · Sincronización Bokun — Mostrando México" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: BokunSyncPage,
});

type AttemptLogEntry = {
  ts: string;
  method: string;
  path: string;
  attempt: number;
  status: number | null;
  elapsedMs: number;
  outcome: "ok" | "retry" | "error";
  reason?: string;
  waitMs?: number;
  retryAfterSec?: number;
};

type SyncStats = {
  ok: boolean;
  productCount: number;
  availableCount: number;
  currencies: string[];
  priceRange: { min: number; max: number } | null;
  elapsedMs: number;
  syncedAt: string;
  error: string | null;
  retries: number;
  rateLimited: number;
  serverErrors: number;
  networkErrors: number;
  requests: number;
  totalAttempts: number;
  failures: number;
  attempts: AttemptLogEntry[];
};

function BokunSyncPage() {
  const refresh = useServerFn(refreshBokunCache);
  const qc = useQueryClient();
  const [log, setLog] = useState<SyncStats[]>([]);

  const mut = useMutation({
    mutationFn: () => refresh() as Promise<SyncStats>,
    onSuccess: async (stats) => {
      setLog((prev) => [stats, ...prev].slice(0, 10));
      // Invalidate any Bokun-related queries
      await qc.invalidateQueries({ queryKey: ["bokun"] });
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl font-semibold">Sincronización Bokun</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Limpia la caché del servidor y vuelve a consultar productos, precios y disponibilidad desde Bokun.
            </p>
          </div>
          <Link
            to="/admin/bokun-status"
            className="text-sm text-muted-foreground underline hover:text-foreground"
          >
            Ver estado Bokun →
          </Link>
        </div>

        <div className="mt-8 rounded-2xl border bg-card p-6 shadow-sm">
          <Button
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
            size="lg"
          >
            {mut.isPending ? "Sincronizando…" : "Sincronizar ahora"}
          </Button>

          {mut.error && (
            <p className="mt-3 text-sm text-destructive">
              {(mut.error as Error).message ?? "Error al sincronizar."}
            </p>
          )}

          {mut.data && (
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Stat label="Productos" value={String(mut.data.productCount)} />
              <Stat label="Disponibles" value={String(mut.data.availableCount)} />
              <Stat label="Monedas" value={mut.data.currencies.join(", ") || "—"} />
              <Stat
                label="Tiempo"
                value={`${(mut.data.elapsedMs / 1000).toFixed(2)} s`}
              />
              {mut.data.priceRange && (
                <Stat
                  label="Rango precios"
                  value={`${mut.data.priceRange.min.toFixed(2)} – ${mut.data.priceRange.max.toFixed(2)}`}
                />
              )}
            </div>
          )}

          {mut.data && (
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Stat label="Solicitudes" value={String(mut.data.requests)} />
              <Stat label="Intentos" value={String(mut.data.totalAttempts)} />
              <Stat label="Reintentos" value={String(mut.data.retries)} />
              <Stat label="Rate-limit (429)" value={String(mut.data.rateLimited)} />
              <Stat label="Errores servidor (5xx)" value={String(mut.data.serverErrors)} />
              <Stat label="Errores red" value={String(mut.data.networkErrors)} />
              <Stat label="Fallos definitivos" value={String(mut.data.failures)} />
            </div>
          )}

          {mut.data && mut.data.attempts.length > 0 && (
            <details className="mt-6">
              <summary className="cursor-pointer text-sm font-semibold text-muted-foreground">
                Bitácora de intentos ({mut.data.attempts.length})
              </summary>
              <div className="mt-3 max-h-96 overflow-auto rounded-lg border bg-background">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="px-2 py-1.5">#</th>
                      <th className="px-2 py-1.5">Método</th>
                      <th className="px-2 py-1.5">Endpoint</th>
                      <th className="px-2 py-1.5">Estado</th>
                      <th className="px-2 py-1.5">ms</th>
                      <th className="px-2 py-1.5">Resultado</th>
                      <th className="px-2 py-1.5">Espera</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mut.data.attempts.map((a, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-2 py-1 font-mono">{a.attempt}</td>
                        <td className="px-2 py-1 font-mono">{a.method}</td>
                        <td className="px-2 py-1 font-mono truncate max-w-[260px]" title={a.path}>{a.path}</td>
                        <td className="px-2 py-1 font-mono">{a.status ?? "—"}</td>
                        <td className="px-2 py-1 font-mono">{a.elapsedMs}</td>
                        <td className={`px-2 py-1 font-mono ${
                          a.outcome === "ok" ? "text-emerald-600" :
                          a.outcome === "retry" ? "text-amber-600" : "text-destructive"
                        }`}>
                          {a.outcome}{a.reason ? ` · ${a.reason}` : ""}
                        </td>
                        <td className="px-2 py-1 font-mono">
                          {a.waitMs ? `${a.waitMs}ms` : "—"}
                          {a.retryAfterSec ? ` (RA ${a.retryAfterSec}s)` : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>

        {log.length > 0 && (
          <div className="mt-8">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Historial de sincronizaciones (sesión)
            </h2>
            <ul className="mt-3 divide-y rounded-xl border bg-card">
              {log.map((s, i) => (
                <li key={i} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                  <span className="font-mono text-xs text-muted-foreground">
                    {new Date(s.syncedAt).toLocaleString()}
                  </span>
                  <span>
                    {s.ok ? (
                      <>
                        {s.productCount} productos · {s.availableCount} disponibles ·{" "}
                        {(s.elapsedMs / 1000).toFixed(2)}s
                      </>
                    ) : (
                      <span className="text-destructive">Error: {s.error}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border bg-background p-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}
